import clsx from "clsx";

interface ProfileHeaderProps {
  displayName: string;
  photoUrl?: string;
}

/**
 * Profile header component displaying user's photo and name.
 *
 * @example
 * ```tsx
 * <ProfileHeader displayName="John Doe" photoUrl="https://example.com/photo.jpg" />
 * ```
 */
export default function ProfileHeader({ displayName, photoUrl }: ProfileHeaderProps) {
  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Profile Photo */}
      <div className="relative">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt="Profile"
            className={clsx(
              // Base layout & dimensions
              "w-32 h-32 rounded-full object-cover",
              // Visual appearance
              "border-4 border-pink-600"
            )}
            data-testid="profile-photo"
          />
        ) : (
          <div
            className={clsx(
              // Base layout & dimensions
              "w-32 h-32 rounded-full",
              // Visual appearance
              "bg-gray-300 border-4 border-pink-600",
              // Flexbox
              "flex items-center justify-center"
            )}
            data-testid="profile-photo-placeholder"
          >
            <span className="text-4xl text-gray-600">{displayName.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>

      {/* Display Name */}
      <h1 className="text-3xl font-bold font-mono text-gray-900" data-testid="profile-header-name">
        {displayName}
      </h1>
    </div>
  );
}
